// Modern localStorage wrapper with TypeScript support
class LocalStorageService {
  set<T>(key: string, value: T): void {
    try {
      const serializedValue = JSON.stringify(value)
      localStorage.setItem(key, serializedValue)
    } catch (error) {
      console.error(`Failed to store item with key "${key}":`, error)
    }
  }

  get<T>(key: string, defaultValue?: T): T | null {
    try {
      const item = localStorage.getItem(key)
      if (item === null) {
        return defaultValue || null
      }
      return JSON.parse(item) as T
    } catch (error) {
      console.error(`Failed to retrieve item with key "${key}":`, error)
      return defaultValue || null
    }
  }

  has(key: string): boolean {
    return localStorage.getItem(key) !== null
  }

  delete(key: string): boolean {
    try {
      localStorage.removeItem(key)
      return true
    } catch (error) {
      console.error(`Failed to delete item with key "${key}":`, error)
      return false
    }
  }

  deleteAll(): void {
    try {
      localStorage.clear()
    } catch (error) {
      console.error('Failed to clear localStorage:', error)
    }
  }

  // Get all keys
  keys(): string[] {
    return Object.keys(localStorage)
  }

  // Get storage size (approximate)
  size(): number {
    let size = 0
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        size += localStorage[key].length + key.length
      }
    }
    return size
  }
}

// Create singleton instance
const localStorageService = new LocalStorageService()

// Export for backward compatibility
export const LocalStorage = {
  set: <T>(key: string, value: T) => localStorageService.set(key, value),
  get: <T>(key: string, defaultValue?: T) => localStorageService.get(key, defaultValue),
  has: (key: string) => localStorageService.has(key),
  delete: (key: string) => localStorageService.delete(key),
  deleteAll: () => localStorageService.deleteAll(),
}
